import { RootStore } from "./stores/rootStore";
import { NavigateFunction } from "react-router-dom";
import onmessage from "./messageHandlers";
import i18n from "./i18n";

/**
 * WebView Bridge for React Native <-> Web App communication
 * Handles push notifications and messages from the native wrapper
 */
class WebViewBridge {
  private static instance: WebViewBridge | null = null;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private pushTokenListener: ((token: string) => void) | null = null;
  private cachedToken: string | null = null;
  private tokenCallbacks: Array<(token: string) => void> = [];
  /** Persistent listeners that survive token delivery (for FCM refresh) */
  private persistentTokenListeners: Array<(token: string) => void> = [];
  /** Recently seen frame _ids, for dropping duplicate deliveries */
  private seenFrameIds: Set<string> = new Set();
  private seenFrameIdOrder: string[] = [];
  private printReceiptListener: ((error?: string) => void) | null = null;

  private constructor() {}

  static getInstance(): WebViewBridge {
    if (!WebViewBridge.instance) {
      WebViewBridge.instance = new WebViewBridge();
    }
    return WebViewBridge.instance;
  }

  /**
   * Initialize the WebView bridge to listen for messages from React Native
   * @param store - MobX root store
   * @param navigate - React Router navigate function
   */
  initialize(store: RootStore, navigate: NavigateFunction) {
    // Remove any existing listener
    this.cleanup();

    // Create and attach the message listener
    this.messageListener = (event: MessageEvent) => {
      // Ignore messages that are not strings (React Native WebView always posts strings)
      if (typeof event.data !== "string") return;

      // Frames injected by react-native-webview are synthesized events with no
      // source window and an empty origin. A genuine cross-window postMessage
      // (e.g. from an embedded iframe) carries both — reject it so page-embedded
      // content can't spoof bridge frames like PUSH_NOTIFICATION.
      if (event.source) return;
      if (event.origin && event.origin !== window.location.origin) return;

      // Ignore empty or ping/pong messages
      if (!event.data || event.data === "pong" || event.data === "ping") return;

      console.log("WebView message received:", event.data);

      try {
        const messageData = JSON.parse(event.data);

        // The listener is attached to both window and document (see below), and
        // some Android WebViews dispatch the same event to both targets. Native
        // stamps every frame with a unique _id — drop repeats.
        if (
          messageData?._id != null &&
          this.isDuplicateFrame(String(messageData._id))
        ) {
          return;
        }

        // Handle push token response (proactive from native or in response to REQUEST_PUSH_TOKEN)
        if (messageData?.type === "PUSH_TOKEN_RESPONSE") {
          if (messageData.error) {
            console.warn("Push token response error:", messageData.error);
            return;
          }
          if (typeof messageData.data === "string") {
            this.cachedToken = messageData.data;
            console.log("Push token received and cached");

            // Resolve the pending requestPushToken() promise if any
            if (this.pushTokenListener) {
              this.pushTokenListener(messageData.data);
              this.pushTokenListener = null;
            }

            // Resolve any queued one-shot callbacks
            const callbacks = [...this.tokenCallbacks];
            this.tokenCallbacks = [];
            callbacks.forEach((cb) => cb(messageData.data));

            // Notify persistent listeners (survives token refresh)
            this.persistentTokenListeners.forEach((cb) => cb(messageData.data));
          }
          return;
        }

        // Handle push notification payloads forwarded from native
        if (messageData?.type === "PUSH_NOTIFICATION" && messageData.data) {
          console.log(
            "Received push notification from native:",
            messageData.data,
          );
          const adaptedMessage = this.adaptPushPayload(messageData.data);
          onmessage(adaptedMessage, store, navigate);
          return;
        }

        if (messageData?.type === "PRINT_RECEIPT_RESPONSE") {
          if (this.printReceiptListener) {
            this.printReceiptListener(
              typeof messageData.error === "string"
                ? messageData.error
                : undefined,
            );
            this.printReceiptListener = null;
          }
          return;
        }

        // Direct message from RN (no type wrapper) — treat as MessageType payload
        if (this.isMessageTypePayload(messageData)) {
          const adaptedMessage = this.adaptPushPayload(messageData);
          onmessage(adaptedMessage, store, navigate);
          return;
        }

        console.warn("Unrecognized WebView message format:", messageData);
      } catch (error) {
        console.error("Error processing WebView message:", error);
      }
    };

    // Listen for messages from React Native WebView
    window.addEventListener("message", this.messageListener);
    // Some Android devices use 'document' instead of 'window'
    document.addEventListener("message", this.messageListener as EventListener);

    // Signal to the native app that the WebView JS is ready to receive messages
    this.postMessage({ type: "WEBVIEW_READY" });
    this.syncLanguage();

    console.log("WebView bridge initialized");
  }

  /**
   * Track a frame _id; returns true if it was already seen.
   * Keeps a bounded window of recent ids.
   */
  private isDuplicateFrame(id: string): boolean {
    if (this.seenFrameIds.has(id)) {
      return true;
    }
    this.seenFrameIds.add(id);
    this.seenFrameIdOrder.push(id);
    if (this.seenFrameIdOrder.length > 200) {
      const evicted = this.seenFrameIdOrder.splice(0, 100);
      evicted.forEach((old) => this.seenFrameIds.delete(old));
    }
    return false;
  }

  /**
   * Check if data looks like a valid notification payload from the backend.
   */
  private isMessageTypePayload(data: any): boolean {
    return (
      data != null &&
      typeof data === "object" &&
      ("type" in data ||
        "data" in data ||
        "audio" in data ||
        "navigation" in data)
    );
  }

  /**
   * Adapt a push notification payload into the MessageEvent format
   * that the onmessage handler expects.
   */
  private adaptPushPayload(payload: any): MessageEvent {
    const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
    return { data: { data: parsed } } as MessageEvent;
  }

  /**
   * Send a message back to React Native
   */
  postMessage(message: any) {
    if (window.ReactNativeWebView) {
      const payload =
        typeof message === "string" ? message : JSON.stringify(message);
      window.ReactNativeWebView.postMessage(payload);
      console.log("Message sent to React Native:", payload);
    } else {
      console.warn(
        "ReactNativeWebView not available. Running in browser mode.",
      );
    }
  }

  /**
   * Check if the app is running inside a React Native WebView
   */
  isReactNative(): boolean {
    return typeof window.ReactNativeWebView !== "undefined";
  }

  /**
   * Get the push token — returns immediately if cached (proactively sent by native),
   * otherwise requests it from native with retry.
   */
  requestPushToken(): Promise<string> {
    // Return cached token immediately if native already sent it proactively
    if (this.cachedToken) {
      return Promise.resolve(this.cachedToken);
    }

    if (!this.isReactNative()) {
      return Promise.reject(new Error("ReactNativeWebView not available"));
    }

    return this.requestWithRetry(3, 2000);
  }

  /**
   * Request token from native with retry logic.
   * Each attempt sends REQUEST_PUSH_TOKEN and waits for a response.
   */
  private requestWithRetry(retries: number, delayMs: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const attempt = (remaining: number) => {
        // Check cache before each attempt — native may have sent it proactively
        if (this.cachedToken) {
          resolve(this.cachedToken);
          return;
        }

        this.pushTokenListener = (token: string) => {
          resolve(token);
        };
        this.postMessage({ type: "REQUEST_PUSH_TOKEN" });

        setTimeout(() => {
          // If listener was already resolved (nulled out), we're done
          if (!this.pushTokenListener) return;

          this.pushTokenListener = null;

          if (remaining > 0) {
            console.log(
              `Push token request timed out, retrying (${remaining} left)...`,
            );
            attempt(remaining - 1);
          } else {
            reject(new Error("Push token not available after retries"));
          }
        }, delayMs);
      };

      attempt(retries);
    });
  }

  /**
   * Print a receipt PNG on the thermal printer selected in the native app.
   */
  printReceipt(imageDataUrl: string): Promise<void> {
    if (!this.isReactNative()) {
      return Promise.reject(new Error("ReactNativeWebView not available"));
    }

    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        this.printReceiptListener = null;
        reject(new Error("PRINT_TIMED_OUT"));
      }, 45000);

      this.printReceiptListener = (error?: string) => {
        window.clearTimeout(timeoutId);
        if (error) {
          reject(new Error(error));
          return;
        }
        resolve();
      };

      this.postMessage({ type: "PRINT_RECEIPT", data: imageDataUrl });
    });
  }

  openPrinterSettings(): void {
    this.postMessage({ type: "OPEN_PRINTER_SETTINGS" });
  }

  syncLanguage(language?: string): void {
    this.postMessage({
      type: "SET_LANGUAGE",
      data: language ?? i18n.language,
    });
  }

  /**
   * Register a persistent listener called whenever a token arrives or refreshes.
   * If token is already cached, calls back immediately as well.
   */
  onToken(callback: (token: string) => void) {
    this.persistentTokenListeners.push(callback);
    if (this.cachedToken) {
      callback(this.cachedToken);
    }
  }

  /**
   * Clean up event listeners
   */
  cleanup() {
    if (this.messageListener) {
      window.removeEventListener("message", this.messageListener);
      document.removeEventListener(
        "message",
        this.messageListener as EventListener,
      );
      this.messageListener = null;
      console.log("WebView bridge cleaned up");
    }
    this.pushTokenListener = null;
    this.tokenCallbacks = [];
    this.persistentTokenListeners = [];
    this.cachedToken = null;
    this.printReceiptListener = null;
    this.seenFrameIds.clear();
    this.seenFrameIdOrder = [];
  }
}

// Type augmentation for React Native WebView
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

export default WebViewBridge;
